import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router";

import "@reach/skip-nav/styles.css";
import "./requestFlow.css";
import "./stateSelector.css";

import {
  californiaLikeStates,
  specialPolicyStates,
  statesInfo,
  supportedStates,
  texasLikeStates,
  elegibleAgentStates,
} from "../../utils/statesUS";
import {
  provinces,
  provincesInFrench,
  supportedProvinces,
} from "../../utils/canadaMatrix";

import * as rk from "../../localization/resourceKeys";
import i18n from "../../i18n";

import AgentForm from "../AgentForm/AgentForm";
import BottomLine from "../Footer/BottomLine";
import BronsonDropdown from "../../dropdown/BronsonDropdown";
import MessageBanner from "./MessageBanner";
import Questionnaire from "./Questionnaire";

import { setRequestForm } from "../../redux/requestForm/requestFormAction";
import { loadStateSelectorSuccess } from "../../redux/StateSelector/stateSelectorActions";
import { clearProtectionServicesExperianDetails } from "../../redux/ProtectionServicesExperianForm/protectionServicesExperianAction";

import { Logger } from "../../Logger/logger";

import PropTypes from "prop-types";


const RequestFlow = (props) => {
  const { requestForm, country, language } = props;
  const history = useHistory();
  const selectedFlow = requestForm.step?.selectedFlow;

  const isCheckRequestFlow = selectedFlow === "check";
  const isNewRequestFlow = selectedFlow === "new";
  const isCanada = country === "Canada";

  const [USState, setUSState] = useState();
  const isUS = country === "US";
  const isCalifornia = isUS && USState === "CA";
  const isElegibleAgentState = isUS && elegibleAgentStates.includes(USState);
  const [stateSupported, setStateSupported] = useState();
  const [isSpecialPolicyState, setIsSpecialPolicyState] = useState(false);
  const [selectedStatesOnly, setSelectedStatesOnly] = useState(false);

  const eligibleStates = React.useMemo(() => {
    const filtered = supportedStates.filter(state => !specialPolicyStates.includes(state));
    if (!filtered.includes("NE")) filtered.push("NE");
    return filtered;
  }, []);


  const [onBehalfOf, setOnBehalfOf] = useState();
  const [brandSelected, setBrandSelected] = useState();
  const [customerRelation, setCustomerRelation] = useState();
  const isProtectionServices = brandSelected === i18n.t(rk.BRANDS_OPTION_VWD_APP_DER);

  const [onBehalfOfQuestionReady, setOnBehalfOfQuestionReady] = useState(false);
  const [customerQuestionReady, setCustomerQuestionReady] = useState(false);
  const [relationQuestionReady, setRelationQuestionReady] = useState(false);

  const [isAgentFlow, setIsAgentFlow] = useState(false);
  const [agentInfo, setAgentInfo] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    isValid: false,
  });

  const [isAgentReady, setIsAgentReady] = useState(false);
  const [continueBtnReady, setContinueBtnReady] = useState(false);

  const handleOnBehalfOfQuestion = (item) => {
    setOnBehalfOf(item);
  }

  const handleOnBehalfOfOtherMessage = () => {
    if (texasLikeStates.includes(USState))
      return i18n.t(rk.ON_BEHALF_OTHER_MESSAGE_TX);
    if (californiaLikeStates.includes(USState)) return i18n.t(rk.ON_BEHALF_OTHER_MESSAGE_CA);

    return i18n.t(rk.ON_BEHALF_OTHER_MESSAGE1);
  };

  const handleOnBehalfOfQuestionAlert = () => {
    if (isElegibleAgentState && isAgentFlow) {
      return null;
    }

    if (onBehalfOf === i18n.t(rk.ON_BEHALF_OPTION_OTHER)) {
      return (
        <div className="validation-message">
          <MessageBanner messageArray={[handleOnBehalfOfOtherMessage()]} />
        </div>
      );
    }
    
    if (onBehalfOf === i18n.t(rk.ON_BEHALF_OPTION_HOUSEHOLD)) {
      return (
        <div className="validation-message">
          <MessageBanner messageArray={[
            i18n.t(rk.STATE_SELECTION_HOUSEHOLDER_CONTENT1),
            i18n.t(rk.STATE_SELECTION_HOUSEHOLDER_CONTENT2),
            i18n.t(rk.STATE_SELECTION_HOUSEHOLDER_CONTENT3),
            i18n.t(rk.STATE_SELECTION_HOUSEHOLDER_CONTENT4),
          ]} />
        </div>
      );
    }

    return null;
  };

  const onBehalfOfQuestion = () => {
    const isElegibleAgentStateReady = isElegibleAgentState && isNewRequestFlow && customerQuestionReady;
    const brandAgentReady = brandSelected === i18n.t(rk.BRANDS_OPTION_VWFS_DFS_AFS) || (brandSelected === i18n.t(rk.BRANDS_OPTION_VW_AUDI) && isElegibleAgentState);

    if ((stateSupported && isNewRequestFlow && !isElegibleAgentState) || isElegibleAgentStateReady) {
      return (
        <div className="o-layout__item u-1/1 u-m-none questions-wrapper">
          <Questionnaire
            id="submissionOnBehalfOf"
            name="submissionOnBehalfOf"
            radioQuestion={isElegibleAgentState? i18n.t(rk.ON_BEHALF_QUESTION_CALI_ONLY) : i18n.t(rk.ON_BEHALF_QUESTION)}
            options={[
              i18n.t(rk.ON_BEHALF_OPTION_SELF),
              brandAgentReady? i18n.t(rk.ON_BEHALF_OPTION_OTHER_CA) : i18n.t(rk.ON_BEHALF_OPTION_OTHER),
            ]}
            handleOnClick={handleOnBehalfOfQuestion}
            value={onBehalfOf}
            data-testid="submissionOnBehalfOf"
          />
          {handleOnBehalfOfQuestionAlert()}
        </div>
      );
    }
    return null;
  };

  const handleCustomerQuestion = (brand) => {
    setCustomerRelation();
    setRelationQuestionReady();
    setCustomerQuestionReady(true);
    setBrandSelected(brand);
    if(isElegibleAgentState) setOnBehalfOf(undefined);
  };

  const relationQuestion = () => {
    const showRelationshipQuestion = 
      isCalifornia &&
      !isAgentFlow &&
      customerQuestionReady &&
      (isNewRequestFlow || (isCheckRequestFlow && !isProtectionServices)) &&
      (onBehalfOfQuestionReady || isCheckRequestFlow);
    
    if (!showRelationshipQuestion) {
      return null;
    }
    
    let questionCopy;

    if (isCheckRequestFlow && isSpecialPolicyState) {
      questionCopy = i18n.t(rk.QUESTION_2);
    } else if (isNewRequestFlow && isSpecialPolicyState) {
      questionCopy = i18n.t(rk.QUESTION_4);
    } else {
      questionCopy = i18n.t(rk.QUESTION_2_CHECK);
    }

    return (
      <div
        data-test-id="select-relationship-question"
        className="o-layout__item u-1/1 u-m-none questions-wrapper"
      >
        <Questionnaire
          id="customerRelation"
          name="customerRelation"
          radioQuestion={questionCopy}
          options={[
            i18n.t(rk.QUESTION_2_OPTION_1),
            ...(brandSelected === i18n.t(rk.BRANDS_OPTION_VWD_APP_DER)
              ? []
              : [i18n.t(rk.QUESTION_2_OPTION_2)]),
            i18n.t(rk.QUESTION_2_OPTION_3),
          ]}
          handleOnClick={handleRelationQuestion}
          value={customerRelation}
          data-testid="customerRelation"
          tooltip={
            isElegibleAgentState
              ? {
                  className: "customerRelation-tooltip",
                  content: `
              <p><strong>${i18n.t(
                rk.QUESTION_2_TOOLTIP_CUSTOMER_HEADER
              )}</strong> ${i18n.t(
                    rk.QUESTION_2_TOOLTIP_CUSTOMER_DESCRIPTION
                  )}</p>
              <p><strong>${i18n.t(
                rk.QUESTION_2_TOOLTIP_PERSONNEL_HEADER
              )}</strong> ${i18n.t(
                    rk.QUESTION_2_TOOLTIP_PERSONNEL_DESCRIPTION
                  )}</p>
              <p><strong>${i18n.t(
                rk.QUESTION_2_TOOLTIP_REPRESENTATIVE_HEADER
              )}</strong> ${i18n.t(
                    rk.QUESTION_2_TOOLTIP_REPRESENTATIVE_DESCRIPTION
                  )}</p>
            `,
                }
              : null
          }
          disabledOptions={
            brandSelected === i18n.t(rk.BRANDS_OPTION_VWD_APP_DER) &&
            texasLikeStates.includes(USState)
              ? [
                  i18n.t(rk.QUESTION_2_OPTION_2),
                  i18n.t(rk.QUESTION_2_OPTION_3),
                ]
              : []
          }
        />
      </div>
    );
  };

  const handleRelationQuestion = (item) => {
    setCustomerRelation(item);
    setRelationQuestionReady(true);
  };

  const customerQuestion = () => {
    const isNewRequestFlowReady =
      isNewRequestFlow && isUS && (isElegibleAgentState || onBehalfOfQuestionReady);
    const isCheckRequestFlowReady = isCheckRequestFlow && (isSpecialPolicyState || supportedStates.includes(USState));

    const disabledOptions = [];
    let defaultSelected = "";

    if (isNewRequestFlowReady || isCheckRequestFlowReady) {
      let question;

      if (isNewRequestFlowReady && isElegibleAgentState) {
        question = i18n.t(rk.BENTLEY_CUSTOMER_QUESTION_CALI_ONLY);
      } else if (isCheckRequestFlowReady) {
        question = i18n.t(rk.BENTLEY_CUSTOMER_QUESTION);
        defaultSelected = i18n.t(rk.BRANDS_OPTION_VW_AUDI);
      } else {
        question = i18n.t(rk.BENTLEY_CUSTOMER_QUESTION_VI_ONLY);
      }

      let options = [];
      
      if (isSpecialPolicyState) {
        options = [
          i18n.t(rk.BRANDS_OPTION_VWFS_DFS_AFS),
          i18n.t(rk.BRANDS_OPTION_VWD_APP_DER),
          i18n.t(rk.BENTLEY_CUSTOMER_OPTION_BENTLEY),
        ]
      } else if (texasLikeStates.includes(USState)) {
        options = [
          i18n.t(rk.BRANDS_OPTION_VW_AUDI),
          i18n.t(rk.BRANDS_OPTION_VWD_APP_DER),
          i18n.t(rk.BENTLEY_CUSTOMER_OPTION_BENTLEY),
        ];
      } else {
        options = [
          i18n.t(rk.BRANDS_OPTION_VW_AUDI),
          i18n.t(rk.BENTLEY_CUSTOMER_OPTION_BENTLEY),
        ];
      }
      if(isCheckRequestFlowReady){
        disabledOptions.push(i18n.t(rk.BENTLEY_CUSTOMER_OPTION_BENTLEY));
      };

      return (
        <div
          data-test-id="select-brand-question"
          className="o-layout__item u-1/1 u-m-none questions-wrapper"
        >
          <Questionnaire
            id="bentleyCustomer"
            name="bentleyCustomer"
            radioQuestion={question}
            options={options}
            handleOnClick={handleCustomerQuestion}
            value={brandSelected || defaultSelected}
            data-testid="bentleyCustomer"
            disabledOptions = {disabledOptions}
            optionsTooltip = {
              {
                className: "customer-options-tooltip",
                content: `
                  <p>
                      ${i18n.t(rk.BENTLEY_CUSTOMER_DISABLED_OPTION_TOOLTIP)}
                    <a href="mailto:DPO@Bentley.co.uk">
                      ${i18n.t(rk.BENTLY_CUSTOMER_DISABLED_OPTION_TOOLTIP_MAIL)}
                    </a>
                  </p>`
              }
            } 
          />
        </div>
      );
    }
    return null;
  };

  const handleAgentInfo = ({
    firstName,
    lastName,
    phone,
    isValid,
  }) => {
    setAgentInfo({
      firstName,
      lastName,
      phone,
      isValid,
    });
    
    setIsAgentReady(
      Boolean(firstName?.trim()) &&
      Boolean(lastName?.trim()) &&
      Boolean(phone?.trim()) &&
      isValid
    );
  }

  const agentForm = () => {
    return isAgentFlow && <AgentForm {...{handleAgentInfo}} />;
  }

  const handleStateAlert = () => {
    if (stateSupported === false && USState !== undefined) {
      return (
        <div className="validation-message">
          <MessageBanner messageArray={[
            i18n.t(rk.QUESTIONNAIRE_MESSAGE_STATE_1),
            i18n.t(rk.UNSUPPORTED_STATE_MESSAGE),
          ]} />
        </div>
      );
    }
    return null;
  };

  const handleContinueButton = () => {
    const isCheckFlowInEligibleState =
      isCheckRequestFlow && eligibleStates.includes(USState);

    const isCheckFlowWithProtection =
      isCheckRequestFlow && isProtectionServices && customerQuestionReady;


    const isCaliforniaWithoutAgentWithRelation =
      isCalifornia && !isAgentFlow && relationQuestionReady;

    const isCaliforniaWithAgent = isElegibleAgentState && isAgentFlow;

    const isCaliforniaCheckFlowValid =
      isCalifornia &&
      isCheckRequestFlow &&
      customerQuestionReady &&
      (isProtectionServices || relationQuestionReady);

    const isSpecialNonCaliforniaWithRelation =
      isSpecialPolicyState &&
      !isCalifornia &&
      relationQuestionReady;

    const isNonSpecialWithCustomerReady =
      !isCalifornia && customerQuestionReady && onBehalfOfQuestionReady && onBehalfOf !== i18n.t(rk.ON_BEHALF_OPTION_OTHER);

    const isTXLikeStatesCheckFlowValid = texasLikeStates.includes(USState) && customerQuestionReady && isCheckRequestFlow;

    const isCanadaWithOnBehalf =
      isCanada && onBehalfOfQuestionReady;

    if (
      isCheckFlowInEligibleState ||
      isCheckFlowWithProtection ||
      isCaliforniaWithoutAgentWithRelation ||
      isCaliforniaWithAgent ||
      isCaliforniaCheckFlowValid ||
      isSpecialNonCaliforniaWithRelation ||
      isNonSpecialWithCustomerReady ||
      isCanadaWithOnBehalf ||
      isTXLikeStatesCheckFlowValid
    ) {
      const payload = {
        supportedState: USState,
        onBehalfOf: onBehalfOf,
        customerRelation: customerRelation,
        brand: brandSelected,
        selectedFlow: selectedFlow,
      };
      const value = {
        currentStateResidence: USState,
        submissionOnBehalfOf: onBehalfOf,
        customerRelation: customerRelation,
        brandAffiliation: brandSelected || "",
        selectedFlow: selectedFlow,
      };

      if(isAgentFlow) {
        payload.agentInfo = agentInfo;
        value.agentInfo = agentInfo;
        value.isAgentSubmission = true;
      } else {
        payload.agentInfo = null;
        value.agentInfo = null;
        value.isAgentSubmission = false;
      }

      return (
        <div className="o-layout">
          <div className="o-layout__item">
            <button
              data-test-id="request-flow-btn"
              className="c-btn u-block u-block-center"
              onClick={() => {
                props.loadStateSelectorSuccess(value);
                props.setRequestForm(payload);
                isProtectionServices && isCheckRequestFlow
                  ? history.push({ pathname: "/verificationForm" })
                  : history.push({ pathname: "/requestselector" });
                if (isNewRequestFlow) {
                  Logger.info(
                    "Qualified New Request",
                    "Location ==> /requestqualifier",
                    "Qualified Request"
                  );
                }
              }}
              disabled={!continueBtnReady}
            >
              <span className="c-btn__text">{i18n.t(rk.BUTTON.CONTINUE)}</span>
            </button>
          </div>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    props.clearProtectionServicesExperianDetails();
  }, [props]);
  useEffect(() => {
    if (isNewRequestFlow) {
      Logger.info(
        `Initiated privacy rights request`,
        `Location ==> ${window.location.pathname}`,
        "User initiated new privacy rights request"
      );
    }
  }, [isNewRequestFlow]);

  useEffect(() => {
    if (USState !== undefined) {
      setIsSpecialPolicyState(specialPolicyStates.includes(USState));
      setSelectedStatesOnly(eligibleStates.includes(USState));

      if (isNewRequestFlow && !supportedStates.includes(USState)) {
        Logger.info(
          "Unqualified New Request => Unsupported State",
          "Location ==> /requestqualifier",
          "Unsupported State"
        );
      }
    }
  }, [USState, isNewRequestFlow, eligibleStates]);

  useEffect(() => {
    if (isNewRequestFlow && onBehalfOf !== undefined) {
      if (onBehalfOf === i18n.t(rk.ON_BEHALF_OPTION_OTHER) || onBehalfOf === i18n.t(rk.ON_BEHALF_OPTION_OTHER_CA)) {
        Logger.info(
          "Unqualified New Request => Completing form for someone else",
          "Location ==> /requestqualifier",
          "Completing form for someone else"
        );
      }
      if (onBehalfOf === i18n.t(rk.ON_BEHALF_OPTION_HOUSEHOLD)) {
        Logger.info(
          "Unqualified New Request => Completing form for a household",
          "Location ==> /requestqualifier",
          "Completing form for a household"
        );
      }
    }

    if (onBehalfOf === i18n.t(rk.ON_BEHALF_OPTION_SELF) && stateSupported) {
      setOnBehalfOfQuestionReady(true);
      setIsAgentFlow(false);
    } else if (isElegibleAgentState) {
      const isAgentFlowReady = onBehalfOf === i18n.t(rk.ON_BEHALF_OPTION_OTHER_CA);
      setCustomerRelation(undefined);
      setOnBehalfOfQuestionReady(isAgentFlowReady);
      setIsAgentFlow(isAgentFlowReady);
    } else {
      setOnBehalfOfQuestionReady(false);
      setCustomerQuestionReady(false);
    }

    if(!isNewRequestFlow) {
      setCustomerQuestionReady(Boolean(brandSelected && typeof brandSelected === "string" && brandSelected.trim()));
    }

  }, [onBehalfOf, brandSelected, isNewRequestFlow, stateSupported, isCalifornia, isElegibleAgentState]);

  useEffect(() => {
    if (
      supportedStates.includes(USState) ||
      supportedProvinces.includes(USState)
    ) {
      setStateSupported(true);
    } else {
      setStateSupported(false);
      setOnBehalfOf("");
      setCustomerRelation(undefined);
      setBrandSelected(undefined);
    }

    setIsAgentFlow(false);
  }, [USState, stateSupported]);

  useEffect(() => {
    if (typeof customerRelation === "string") {
      setRelationQuestionReady(true);
    } else {
      setRelationQuestionReady(false);
    }
  }, [customerRelation]);

  useEffect(() => {
    const isCaliforniaFlow =
      isSpecialPolicyState &&
      stateSupported &&
      onBehalfOfQuestionReady &&
      customerQuestionReady &&
      (relationQuestionReady || (isAgentFlow && isAgentReady));

    const isNonCaliforniaFlow =
      !isCalifornia &&
      stateSupported &&
      onBehalfOfQuestionReady &&
      customerQuestionReady &&
      (!isElegibleAgentState || !isAgentFlow || isAgentReady);


    const isCheckFlowInSpecialPolicyNonCalifornia =
      isCheckRequestFlow &&
      !isCalifornia &&
      isSpecialPolicyState &&
      customerQuestionReady;

    const isCheckFlowInCalifornia =
      isCheckRequestFlow &&
      isCalifornia &&
      customerQuestionReady &&
      (relationQuestionReady || isProtectionServices);

    const isCheckFlowWithSelectedStatesOnly =
      isCheckRequestFlow && selectedStatesOnly;

    const isCanadaFlow =
      isCanada && onBehalfOfQuestionReady;


    const shouldEnableBtn =
      isCaliforniaFlow ||
      isNonCaliforniaFlow ||
      isCheckFlowInSpecialPolicyNonCalifornia ||
      isCheckFlowInCalifornia ||
      isCheckFlowWithSelectedStatesOnly ||
      isCanadaFlow

    setContinueBtnReady(shouldEnableBtn);

  }, [
    onBehalfOfQuestionReady,
    stateSupported,
    customerQuestionReady,
    relationQuestionReady,
    isCheckRequestFlow,
    selectedStatesOnly,
    isCanada,
    isSpecialPolicyState,
    USState,
    isProtectionServices,
    isAgentFlow,
    isAgentReady,
    isCalifornia,
    isElegibleAgentState,
    brandSelected
  ]);

  useEffect(() => {
    if (selectedFlow === undefined) {
      history.push("/");
    }
  }, [history, selectedFlow]);

  return (
    <div data-test-id="questionnaire" className="request-flow-wrapper">
      <div className="o-layout__item u-1/1 u-m-none u-p-none u-mb-small state-content">
        <div className="request-form-text">
          <p>
            {isCheckRequestFlow
              ? i18n.t(rk.STATE_SELECTION_CONTENT5_CHECK_STATUS)
              : i18n.t(rk.STATE_SELECTION_CONTENT5_SUBMIT)}
          </p>
        </div>
      </div>

      <div className="o-layout__item u-1/1 u-m-none first-question-wrapper">
        <div data-test-id="select-state-question" id="first-question">
          {i18n.t(rk.STATE_QUESTION)}
        </div>

        <BronsonDropdown
          dataTestId="select-state-dropdown"
          data-testid="select-state-question"
          options={
            country === "Canada"
              ? language.toUpperCase() === "FRA"
                ? provincesInFrench
                : provinces
              : statesInfo
          }
          setOptionState={(item) => setUSState(item)}
          value={USState}
          placeholder={i18n.t(rk.COMMON.SELECT_STATE)}
        />

        {handleStateAlert()}
      </div>

      {/* TODO: remove second condition when all toggles be released */}
      {isElegibleAgentState? <>
        {customerQuestion()}
        {onBehalfOfQuestion()}
        {agentForm()}
        {relationQuestion()}
      </> : <>
        {onBehalfOfQuestion()}
        {customerQuestion()}
      </>}

      <div className="continue-btn-wrapper">
        <div className="u-mv continue-btn-div">
          <div className="u-mt">{handleContinueButton()}</div>
        </div>
      </div>

      <div className="request-footer-wrapper">
        <div
          id="request-footer-content"
          className={`${stateSupported && "request-footer-content-2"}`}
        >
          <div id="request-footer-text">
            {i18n.t(rk.VERFICIATION_FORM.NOTE2)}
          </div>
          <BottomLine />
        </div>
      </div>
    </div>
  );
};

RequestFlow.propTypes = {
  requestForm: {
    step: PropTypes.shape({
      selectedFlow: PropTypes.string,
    }),
  },
  country: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  loadStateSelectorSuccess: PropTypes.func.isRequired,
  setRequestForm: PropTypes.func.isRequired,
  clearProtectionServicesExperianDetails: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  return {
    requestForm: state.requestForm,
    country: state.initialReducer.country,
    language: state.initialReducer.language,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setRequestForm: (payload) => dispatch(setRequestForm(payload)),
    loadStateSelectorSuccess: (value) =>
      dispatch(loadStateSelectorSuccess(value)),
    clearProtectionServicesExperianDetails: () =>
      dispatch(clearProtectionServicesExperianDetails()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(RequestFlow);
